import React from "react";
import { createContext } from "react";

import { AppTheme, Network } from "../../types";
import { useLocalStorage } from "usehooks-ts";
import { networks } from "../../config/constants";

type AppContextProviderProps = {
  themeMode: AppTheme;
  setThemeMode: React.Dispatch<React.SetStateAction<AppTheme>>;
  children: React.ReactNode;
};

type AppState = {
  isLoading: boolean;
  theme: AppTheme;
  network: Network;
  networks: Network[];
};

interface AppContextData extends AppState {
  selectTheme: (themeMode: AppTheme) => void;
  selectNetwork: (networkId: number) => void;
  addNetwork: (name: string, address: string, description: string) => void;
  removeNetwork: (id: number) => void;
}

const initialState: AppState = {
  isLoading: true,
  theme: "dark",
  network: networks.mainnet,
  networks: [networks.mainnet, networks.testnet],
};

export const AppContext = createContext<AppContextData>({
  ...initialState,
  selectTheme: () => null,
  selectNetwork: () => null,
  addNetwork: () => null,
  removeNetwork: () => null,
});

export const AppContextProvider: React.FC<AppContextProviderProps> = ({ children, themeMode, setThemeMode }) => {
  const [state, setState] = useLocalStorage<AppState>("state", initialState);

  const selectTheme = (themeMode: AppTheme) => {
    setThemeMode(themeMode);
  };

  const selectNetwork = (networkId: number) => {
    let network = state.networks.find((n) => n.id === networkId);

    setState((prev) => ({ ...prev, network: network || networks.mainnet }));
  };

  const addNetwork = (name: string, address: string, description: string) => {
    const newNetwork: Network = {
      id: Date.now(),
      type: "custom",
      name,
      address,
      description,
    };

    const networks = [...state.networks, newNetwork];

    setState((prev) => ({ ...prev, networks: networks }));
  };

  const removeNetwork = (id: number) => {
    const networks = state.networks.filter((n) => n.id !== id);

    setState((prev) => ({ ...prev, networks: networks }));

    if (state.network.id === id) {
      selectNetwork(state.networks[0].id);
    }
  };

  return (
    <AppContext.Provider
      value={{
        ...{ ...state, theme: themeMode },
        selectTheme,
        selectNetwork,
        addNetwork,
        removeNetwork,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
